import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, FileUp, Pencil, Plus, Trash2 } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import { useCenter, useCenterCourses, useDeleteCourse, useUpdateCourse, COURSE_STATUSES, CenterCourse, CourseStatus } from "../../../center";
import CenterAdminShell from "../components/CenterAdminShell";
import CourseFormModal from "../components/CourseFormModal";
import { COURSE_ERRORS } from "../refusals";
import CourseImportModal from "../components/CourseImportModal";

const STATUS_STYLES: Record<CourseStatus, string> = {
    planned: "bg-gray-100 text-gray-700 dark:bg-gray-500/10 dark:text-gray-300",
    active: "bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400",
    finished: "bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300",
    cancelled: "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400",
};

/**
 * The courses a training centre runs.
 *
 * Listed by their internal name — how the staff call them — with the public
 * one under it, their plan, when they run and where they stand, and how many
 * students are in each: a count, never who. Ids stay the software's own: a
 * spreadsheet names a course by its internal name.
 */
export default function CenterCoursesPage() {
    const { t, i18n } = useTranslation();
    const { center, loading, isAdmin } = useCenter();
    const isTraining = center?.type === "training";
    const {
        courses,
        loading: coursesLoading,
        failed: coursesFailed,
    } = useCenterCourses(center?.id, isTraining);
    const update = useUpdateCourse(center?.id);
    const remove = useDeleteCourse(center?.id);

    const [error, setError] = useState<string | null>(null);
    // The course being edited; `null` is a new one, `undefined` no dialog.
    const [editing, setEditing] = useState<CenterCourse | null | undefined>(undefined);
    // The course a new one is being started from, if any.
    const [copying, setCopying] = useState<CenterCourse | undefined>(undefined);
    const [importing, setImporting] = useState(false);

    if (loading || !center) {
        return <p className="p-6 text-sm text-gray-500">{t("common.loading")}</p>;
    }

    const refuse = (err: unknown) => {
        const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
        setError(t((detail && COURSE_ERRORS[detail]) || "center.courses.errorGeneric"));
    };

    const changeStatus = async (course: CenterCourse, next: CourseStatus) => {
        setError(null);
        try {
            await update.mutateAsync({ courseId: course.id, changes: { status: next } });
        } catch (err) {
            refuse(err);
        }
    };

    const handleDelete = async (course: CenterCourse) => {
        if (!window.confirm(t("center.courses.deleteConfirm", { name: course.internal_name }))) return;
        setError(null);
        try {
            await remove.mutateAsync(course.id);
        } catch (err) {
            refuse(err);
        }
    };

    const formatDate = (value: string | null) =>
        value
            ? new Date(`${value}T00:00:00`).toLocaleDateString(i18n.language, {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
              })
            : "—";

    return (
        <CenterAdminShell center={center}>
            <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h2 className="mb-1 text-lg font-semibold text-content-primary">
                            {t("center.courses.title")}
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t("center.courses.subtitle")}</p>
                    </div>
                    {isTraining && isAdmin && (
                        <div className="flex flex-wrap gap-2">
                            <Button
                                variant="outline"
                                onClick={() => setImporting(true)}
                                startIcon={<FileUp size={16} aria-hidden="true" />}
                            >
                                {t("center.csv.courses.button")}
                            </Button>
                            <Button onClick={() => setEditing(null)} startIcon={<Plus size={16} aria-hidden="true" />}>
                                {t("center.courses.newTitle")}
                            </Button>
                        </div>
                    )}
                </div>

                {!isTraining ? (
                    <p className="text-sm text-gray-500">{t("center.courses.errorNotTraining")}</p>
                ) : (
                    <>
                        {error && (
                            <p role="alert" className="mb-4 text-sm text-red-600 dark:text-red-400">
                                {error}
                            </p>
                        )}

                        {coursesLoading ? (
                            <p className="text-sm text-gray-500">{t("common.loading")}</p>
                        ) : coursesFailed ? (
                            // Not "no courses yet": a failed load says nothing
                            // about whether the school has any.
                            <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                                {t("center.courses.errorLoading")}
                            </p>
                        ) : courses.length === 0 ? (
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {t("center.courses.empty")}
                            </p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500 dark:border-gray-800 dark:text-gray-400">
                                            <th scope="col" className="py-2 pr-4 font-medium">
                                                {t("center.courses.name")}
                                            </th>
                                            <th scope="col" className="py-2 pr-4 font-medium">
                                                {t("center.courses.plan.title")}
                                            </th>
                                            <th scope="col" className="py-2 pr-4 font-medium">
                                                {t("center.courses.dates")}
                                            </th>
                                            <th scope="col" className="py-2 pr-4 font-medium">
                                                {t("center.courses.status")}
                                            </th>
                                            <th scope="col" className="py-2 pr-4 text-right font-medium">
                                                {t("center.courses.students")}
                                            </th>
                                            <th scope="col" className="py-2 font-medium">
                                                <span className="sr-only">{t("center.team.actions")}</span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {courses.map((course) => (
                                            <tr
                                                key={course.id}
                                                className="border-b border-gray-100 last:border-0 dark:border-gray-800"
                                            >
                                                <td className="py-3 pr-4">
                                                    <p className="font-medium text-content-primary">
                                                        {course.internal_name}
                                                    </p>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                                        {course.name}
                                                    </p>
                                                </td>
                                                <td className="py-3 pr-4 text-gray-600 dark:text-gray-400">
                                                    {course.phases.length > 0
                                                        ? t("center.courses.plan.count", {
                                                              count: course.phases.length,
                                                          })
                                                        : "—"}
                                                </td>
                                                <td className="whitespace-nowrap py-3 pr-4 text-gray-600 dark:text-gray-400">
                                                    {formatDate(course.starts_on)} – {formatDate(course.ends_on)}
                                                </td>
                                                <td className="py-3 pr-4">
                                                    {isAdmin ? (
                                                        <select
                                                            aria-label={t("center.courses.statusOf", {
                                                                name: course.internal_name,
                                                            })}
                                                            value={course.status}
                                                            onChange={(e) =>
                                                                void changeStatus(
                                                                    course,
                                                                    e.target.value as CourseStatus
                                                                )
                                                            }
                                                            className={`rounded-full border-0 px-2 py-1 text-xs font-medium ${STATUS_STYLES[course.status]}`}
                                                        >
                                                            {COURSE_STATUSES.map((option) => (
                                                                <option key={option} value={option}>
                                                                    {t(`center.courses.statuses.${option}`)}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <span
                                                            className={`rounded-full px-2 py-1 text-xs font-medium ${STATUS_STYLES[course.status]}`}
                                                        >
                                                            {t(`center.courses.statuses.${course.status}`)}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="py-3 pr-4 text-right tabular-nums text-content-primary">
                                                    {course.students}
                                                </td>
                                                <td className="py-3 text-right">
                                                    {isAdmin && (
                                                        <span className="inline-flex gap-1">
                                                            <button
                                                                type="button"
                                                                onClick={() => setEditing(course)}
                                                                aria-label={t("center.courses.edit", {
                                                                    name: course.internal_name,
                                                                })}
                                                                className="inline-flex size-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5"
                                                            >
                                                                <Pencil size={15} aria-hidden="true" />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => setCopying(course)}
                                                                aria-label={t("center.courses.copy", {
                                                                    name: course.internal_name,
                                                                })}
                                                                className="inline-flex size-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5"
                                                            >
                                                                <Copy size={15} aria-hidden="true" />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => void handleDelete(course)}
                                                                aria-label={t("center.courses.delete", {
                                                                    name: course.internal_name,
                                                                })}
                                                                className="inline-flex size-9 items-center justify-center rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10"
                                                            >
                                                                <Trash2 size={15} aria-hidden="true" />
                                                            </button>
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </section>
            {isTraining && isAdmin && (
                <>
                    <CourseFormModal
                        isOpen={editing !== undefined}
                        onClose={() => setEditing(undefined)}
                        centerId={center.id}
                        course={editing}
                    />
                    <CourseFormModal
                        isOpen={copying !== undefined}
                        onClose={() => setCopying(undefined)}
                        centerId={center.id}
                        copyOf={copying}
                    />
                    <CourseImportModal isOpen={importing} onClose={() => setImporting(false)} centerId={center.id} />
                </>
            )}
        </CenterAdminShell>
    );
}
